/**
 * GTIN (Global Trade Item Number) validation utilities
 * Supports UPC (12 digits) and GTIN-14 (14 digits) formats
 * 
 * GS1 Check Digit Algorithm:
 * 1. Take the first 13 digits of the GTIN
 * 2. Starting from the left, multiply every other digit by 3 (positions 1, 3, 5, 7, 9, 11, 13)
 * 3. Add all digits together
 * 4. Calculate: (10 - (sum % 10)) % 10
 * 5. This gives you the expected check digit
 */

/**
 * Converts a 12-digit UPC to a 14-digit GTIN by adding leading zeros
 * @param {string} upc - 12-digit UPC
 * @returns {string} - 14-digit GTIN
 */
export const upcToGtin = (upc) => {
  if (!upc || upc.length !== 12) {
    throw new Error('UPC must be exactly 12 digits');
  }
  return `00${upc}`;
};

/**
 * Validates a GTIN or UPC according to GS1 standards
 * @param {string} gtin - GTIN or UPC to validate
 * @returns {object} - { isValid: boolean, error: string|null }
 */
export const validateGTIN = (gtin) => {
  if (!gtin) {
    return { isValid: true, error: null }; // GTIN/UPC is optional
  }

  // Check if all characters are digits
  if (!/^\d+$/.test(gtin)) {
    return { 
      isValid: false, 
      error: 'GTIN/UPC must contain only digits' 
    };
  }

  // Handle 12-digit UPCs, 13-digit UPCs with single leading zero, and 14-digit GTINs
  let normalizedGtin = gtin;
  if (gtin.length === 12) {
    normalizedGtin = upcToGtin(gtin);
  } else if (gtin.length === 13) {
    // 13 digits with single leading zero are valid UPCs
    normalizedGtin = `0${gtin}`;
  } else if (gtin.length !== 14) {
    return { 
      isValid: false, 
      error: 'GTIN/UPC must be 12-14 digits' 
    };
  }

  // GS1 Check digit validation
  const digits = normalizedGtin.split('').map(Number);
  const checkDigit = digits[13];
  
  // Use the correct GS1 algorithm: multiply by 3 for odd positions (0-indexed)
  // Positions 1, 3, 5, 7, 9, 11, 13 (0-indexed: 1, 3, 5, 7, 9, 11, 13)
  const sum = digits.slice(0, 13).reduce((acc, digit, index) => {
    return acc + (index % 2 === 1 ? digit : digit * 3);
  }, 0);
  
  const calculatedCheckDigit = (10 - (sum % 10)) % 10;

  if (checkDigit !== calculatedCheckDigit) {
    return { 
      isValid: false, 
      error: 'Invalid GTIN/UPC check digit, expected ' + calculatedCheckDigit + ' but got ' + checkDigit 
    };
  }

  return { isValid: true, error: null };
};

/**
 * Normalizes a GTIN/UPC input to a 14-digit GTIN
 * @param {string} input - GTIN or UPC input
 * @returns {string} - Normalized 14-digit GTIN
 */
export const normalizeGTIN = (input) => {
  if (!input) return '';
  
  if (input.length === 12) {
    return upcToGtin(input);
  }
  
  return input;
};

/**
 * Checks if a GTIN is unique within a set of existing GTINs
 * @param {string} gtin - GTIN to check
 * @param {Set} existingGtins - Set of existing GTINs
 * @returns {boolean} - True if unique, false if duplicate
 */
export const isGTINUnique = (gtin, existingGtins) => {
  if (!gtin) return true; // Empty GTINs are always unique
  
  const normalizedGtin = normalizeGTIN(gtin);
  return !existingGtins.has(normalizedGtin);
};

/**
 * Calculates the expected check digit for a 13-digit GTIN prefix
 * @param {string} prefix - 13-digit GTIN prefix (without check digit)
 * @returns {number} - Expected check digit
 */
export const calculateCheckDigit = (prefix) => {
  if (!prefix || prefix.length !== 13) {
    throw new Error('Prefix must be exactly 13 digits');
  }
  
  const digits = prefix.split('').map(Number);
  
  // Use the correct GS1 algorithm: multiply by 3 for odd positions (0-indexed)
  const sum = digits.reduce((acc, digit, index) => {
    return acc + (index % 2 === 1 ? digit : digit * 3);
  }, 0);
  
  const checkDigit = (10 - (sum % 10)) % 10;
  return checkDigit;
};

/**
 * Formats a GTIN or UPC for display
 * @param {string} gtin - GTIN or UPC to format
 * @returns {string} - Formatted GTIN/UPC or original UPC if it has 2 leading zeros
 */
export const formatGTIN = (gtin) => {
  if (!gtin) return '';
  
  // If it's a 14-digit GTIN with 2 leading zeros, treat it as a UPC
  if (gtin.length === 14 && gtin.startsWith('00')) {
    const upc = gtin.slice(2); // Remove the 2 leading zeros
    return upc; // Return as 12-digit UPC
  }
  
  // For other cases, normalize and format
  const normalizedGtin = normalizeGTIN(gtin);
  
  // If it's a 12-digit UPC, return as is
  if (normalizedGtin.length === 12) {
    return normalizedGtin;
  }
  
  // For 14-digit GTINs, add spaces for readability: 00 1234 5678 9012 3
  return normalizedGtin.replace(/(\d{2})(\d{4})(\d{4})(\d{4})(\d)/, '$1 $2 $3 $4 $5');
};

/**
 * Normalizes GTIN for backend storage - always returns 14-digit format
 * 12-digit UPCs are converted to 14-digit GTINs with leading zeros
 * @param {string} input - Raw GTIN or UPC input
 * @returns {string} - 14-digit GTIN for backend storage
 */
export const normalizeGTINForStorage = (input) => {
  if (!input) return '';
  
  // Remove any non-digit characters
  const cleaned = input.replace(/\D/g, '');
  
  // Convert 12-digit UPC to 14-digit GTIN
  if (cleaned.length === 12) {
    return upcToGtin(cleaned);
  }
  
  // Convert 13-digit UPC with single leading zero to 14-digit GTIN
  if (cleaned.length === 13) {
    return `0${cleaned}`;
  }
  
  // Return 14-digit GTIN as-is
  if (cleaned.length === 14) {
    return cleaned;
  }
  
  // Invalid length - return as-is for validation to catch
  return cleaned;
};

/**
 * Compares two GTINs for equality, ignoring leading zeros
 * @param {string} gtin1 - First GTIN
 * @param {string} gtin2 - Second GTIN
 * @returns {boolean} - True if GTINs are equivalent
 */
export const compareGTINs = (gtin1, gtin2) => {
  if (!gtin1 && !gtin2) return true;
  if (!gtin1 || !gtin2) return false;
  
  // Normalize both GTINs to 14-digit format
  const normalized1 = normalizeGTINForStorage(gtin1);
  const normalized2 = normalizeGTINForStorage(gtin2);
  
  return normalized1 === normalized2;
};

/**
 * Checks if a GTIN is unique within a set of existing GTINs
 * Normalizes both the input and existing GTINs for comparison
 * @param {string} gtin - GTIN to check
 * @param {Array|Set} existingGtins - Array or Set of existing GTINs
 * @returns {boolean} - True if unique, false if duplicate
 */
export const isGTINUniqueInList = (gtin, existingGtins) => {
  if (!gtin) return true; // Empty GTINs are always unique
  
  const normalizedInput = normalizeGTINForStorage(gtin);
  
  // Convert to array if it's a Set
  const gtinArray = Array.isArray(existingGtins) ? existingGtins : Array.from(existingGtins);
  
  return !gtinArray.some(existingGtin => {
    if (!existingGtin) return false;
    const normalizedExisting = normalizeGTINForStorage(existingGtin);
    return normalizedInput === normalizedExisting;
  });
};

/**
 * Converts a consumer identifier into canonical EAN-13 storage format.
 * 12-digit UPC values are stored as EAN-13 with a leading zero.
 * @param {string|null|undefined} value - UPC-12 or EAN-13
 * @returns {string|null} - EAN-13 for storage or null for empty
 */
export const toStoredConsumerEan = (value) => {
  if (value === null || value === undefined) return null;

  const cleaned = String(value).replace(/\D/g, '');
  if (!cleaned) return null;

  if (cleaned.length === 12) {
    return `0${cleaned}`;
  }

  if (cleaned.length === 13) {
    return cleaned;
  }

  return cleaned;
};

/**
 * Formats stored consumer EAN for customer-facing display.
 * Show UPC-12 when leading digit is zero, otherwise show EAN-13.
 * @param {string|null|undefined} storedEan
 * @returns {string}
 */
export const toDisplayConsumerIdentifier = (storedEan) => {
  if (!storedEan) return '';

  const cleaned = String(storedEan).replace(/\D/g, '');
  if (cleaned.length === 13 && cleaned.startsWith('0')) {
    return cleaned.slice(1);
  }

  return cleaned;
};
